import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

function formatarData(valor) {
  if (!valor) return '—';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(valor));
  } catch {
    return '—';
  }
}

export default function AdminAcompanhamentos() {
  const [coaches, setCoaches] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [vinculos, setVinculos] = useState([]);

  const [coachSelecionado, setCoachSelecionado] = useState('');
  const [participanteSelecionado, setParticipanteSelecionado] = useState('');

  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');

  const carregarDados = useCallback(async () => {
    try {
      setErro('');

      const response = await fetch(
        '/api/admin/coach-participantes',
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível carregar os acompanhamentos.'
        );
      }

      setCoaches(data.coaches || []);
      setParticipantes(data.participantes || []);
      setVinculos(data.vinculos || []);
    } catch (e) {
      setErro(
        e.message || 'Não foi possível carregar os acompanhamentos.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const vinculosAtivos = useMemo(
    () => vinculos.filter(v => !v.revoked_at),
    [vinculos]
  );

  const mapaCoaches = useMemo(() => {
    return Object.fromEntries(
      coaches.map(coach => [coach.id, coach])
    );
  }, [coaches]);

  const mapaParticipantes = useMemo(() => {
    return Object.fromEntries(
      participantes.map(participante => [
        participante.id,
        participante
      ])
    );
  }, [participantes]);

  async function vincular() {
    if (!coachSelecionado || !participanteSelecionado) {
      setErro('Selecione um Coach e um Participante.');
      return;
    }

    try {
      setErro('');
      setProcessando(true);

      const response = await fetch(
        '/api/admin/coach-participantes',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            coach_user_id: coachSelecionado,
            participant_id: participanteSelecionado
          })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível criar o vínculo.'
        );
      }

      setParticipanteSelecionado('');
      await carregarDados();
    } catch (e) {
      setErro(
        e.message || 'Não foi possível criar o vínculo.'
      );
    } finally {
      setProcessando(false);
    }
  }

  async function revogar(vinculo) {
    const coach = mapaCoaches[vinculo.coach_user_id];
    const participante =
      mapaParticipantes[vinculo.participant_id];

    const confirmar = window.confirm(
      `Desvincular ${participante?.nome || 'Participante'} de ${coach?.nome || 'Coach'}?`
    );

    if (!confirmar) return;

    try {
      setErro('');
      setProcessando(true);

      const response = await fetch(
        `/api/admin/coach-participantes/${vinculo.id}/revogar`,
        {
          method: 'PATCH',
          credentials: 'include'
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível remover o vínculo.'
        );
      }

      await carregarDados();
    } catch (e) {
      setErro(
        e.message || 'Não foi possível remover o vínculo.'
      );
    } finally {
      setProcessando(false);
    }
  }

  const mainStyle = {
    marginLeft: '220px',
    padding: '2rem',
    minHeight: '100vh',
    background: '#f4f4f4'
  };

  return (
    <div>
      <AdminSidebar />

      <main style={mainStyle}>
        <h1
          style={{
            margin: 0,
            color: '#242F3C',
            fontSize: '2rem',
            fontWeight: 700
          }}
        >
          Acompanhamentos
        </h1>

        <p
          style={{
            marginTop: 8,
            marginBottom: 28,
            color: '#697382'
          }}
        >
          Defina quais Participantes serão acompanhados por cada Coach / Mentor / Psicólogo.
        </p>

        {erro && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: '12px 14px',
              borderRadius: 8,
              background: '#FFF3F2',
              color: '#B42318',
              border: '1px solid #FECDCA',
              fontWeight: 600
            }}
          >
            {erro}
          </div>
        )}

        <section
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            marginBottom: 28,
            boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
          }}
        >
          <h2
            style={{
              margin: '0 0 18px 0',
              color: '#242F3C',
              fontSize: '1.25rem'
            }}
          >
            Novo vínculo
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 16,
              alignItems: 'end'
            }}
          >
            <label>
              <div
                style={{
                  marginBottom: 6,
                  fontWeight: 700,
                  color: '#242F3C'
                }}
              >
                Coach / Mentor / Psicólogo
              </div>

              <select
                value={coachSelecionado}
                onChange={e =>
                  setCoachSelecionado(e.target.value)
                }
                disabled={loading || processando}
                style={campoStyle}
              >
                <option value="">
                  Selecione um profissional
                </option>

                {coaches.map(coach => (
                  <option
                    key={coach.id}
                    value={coach.id}
                  >
                    {coach.nome} — {coach.email}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <div
                style={{
                  marginBottom: 6,
                  fontWeight: 700,
                  color: '#242F3C'
                }}
              >
                Participante
              </div>

              <select
                value={participanteSelecionado}
                onChange={e =>
                  setParticipanteSelecionado(e.target.value)
                }
                disabled={loading || processando}
                style={campoStyle}
              >
                <option value="">
                  Selecione um Participante
                </option>

                {participantes.map(participante => (
                  <option
                    key={participante.id}
                    value={participante.id}
                  >
                    {participante.nome}
                    {participante.empresa
                      ? ` — ${participante.empresa}`
                      : ''}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={vincular}
              disabled={
                loading ||
                processando ||
                !coachSelecionado ||
                !participanteSelecionado
              }
              style={{
                background:
                  loading ||
                  processando ||
                  !coachSelecionado ||
                  !participanteSelecionado
                    ? '#999'
                    : '#F36611',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '11px 20px',
                fontWeight: 700,
                cursor:
                  loading ||
                  processando ||
                  !coachSelecionado ||
                  !participanteSelecionado
                    ? 'default'
                    : 'pointer'
              }}
            >
              {processando
                ? 'Processando...'
                : 'Vincular Participante'}
            </button>
          </div>
        </section>

        <section>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 16,
              marginBottom: 14,
              flexWrap: 'wrap'
            }}
          >
            <h2
              style={{
                color: '#242F3C',
                fontSize: '1.25rem',
                margin: 0
              }}
            >
              Vínculos ativos
            </h2>

            <div
              style={{
                color: '#697382',
                fontSize: '0.92rem'
              }}
            >
              {vinculosAtivos.length} vínculo
              {vinculosAtivos.length === 1 ? '' : 's'}
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              overflowX: 'auto',
              boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: 900
              }}
            >
              <thead style={{ background: '#f7f7f7' }}>
                <tr>
                  <th style={th}>Coach</th>
                  <th style={th}>Participante</th>
                  <th style={th}>Empresa</th>
                  <th style={th}>Vinculado em</th>
                  <th style={th}>Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#697382'
                      }}
                    >
                      Carregando acompanhamentos...
                    </td>
                  </tr>
                ) : (
                  vinculosAtivos.map(vinculo => {
                    const coach =
                      mapaCoaches[vinculo.coach_user_id];

                    const participante =
                      mapaParticipantes[
                        vinculo.participant_id
                      ];

                    return (
                      <tr key={vinculo.id}>
                        <td style={td}>
                          <strong>
                            {coach?.nome || '—'}
                          </strong>

                          <div style={subtexto}>
                            {coach?.email || '—'}
                          </div>
                        </td>

                        <td style={td}>
                          <strong>
                            {participante?.nome || '—'}
                          </strong>

                          <div style={subtexto}>
                            {participante?.email || '—'}
                          </div>
                        </td>

                        <td style={td}>
                          {participante?.empresa || '—'}
                        </td>

                        <td style={td}>
                          {formatarData(
                            vinculo.assigned_at
                          )}
                        </td>

                        <td style={td}>
                          <button
                            type="button"
                            onClick={() =>
                              revogar(vinculo)
                            }
                            disabled={processando}
                            style={{
                              background: '#242F3C',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 7,
                              padding: '7px 12px',
                              fontWeight: 700,
                              cursor: processando
                                ? 'default'
                                : 'pointer',
                              opacity: processando
                                ? 0.6
                                : 1
                            }}
                          >
                            Desvincular
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}

                {!loading &&
                  vinculosAtivos.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        style={{
                          padding: '2.5rem',
                          textAlign: 'center',
                          color: '#697382'
                        }}
                      >
                        Nenhum acompanhamento ativo.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

const campoStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 12px',
  borderRadius: 8,
  border: '1px solid #CCD3DA',
  background: '#fff',
  fontSize: '0.95rem'
};

const th = {
  padding: 14,
  borderBottom: '2px solid #e6e6e6',
  textAlign: 'left',
  color: '#242F3C',
  whiteSpace: 'nowrap'
};

const td = {
  padding: 14,
  borderBottom: '1px solid #eef0f2',
  verticalAlign: 'middle'
};

const subtexto = {
  marginTop: 4,
  color: '#697382',
  fontSize: '0.86rem'
};

